// plugins/modal-popstate.client.ts
import { useRouter } from 'vue-router'

export default defineNuxtPlugin(() => {
	if (!process.client) return

	const router = useRouter()

	// 팝업(모달) 오픈 감지용 셀렉터
	const modalSelector =
		'.popup, .full-popup, .gnb-mob, .popup-wrap.full'

	// 페이지 이동 시 히스토리 스택에 추가
	let isInitialLoad = true
	let previousRoute = ''

	// 새로고침 후 히스토리 상태 초기화 방지
	const initializeHistoryState = () => {
		if (window.history.state === null) {
			const raw = router.currentRoute.value.fullPath || window.location.pathname || '/'
			const currentPath = raw.startsWith('/') ? raw : `/${raw}`
			// Vue Router replace 를 사용해서 내부 state(key, idx 등) 보존
			router.replace({ path: currentPath })
		}
	}
	initializeHistoryState()

	router.afterEach((to, from) => {
		// 초기 로드가 아닌 경우에만 히스토리 스택에 추가
		if (
			!isInitialLoad &&
			from.fullPath &&
			from.fullPath !== to.fullPath &&
			from.fullPath.startsWith('/') &&
			!from.fullPath.includes('undefined') &&
			from.fullPath !== previousRoute
		) {
			previousRoute = from.fullPath

			// 절대경로 보장
			const toPath = to.fullPath.startsWith('/') ? to.fullPath : `/${to.fullPath}`

			// 기존 history.state 보존
			const newState = {
				...window.history.state,
				nuxt: true,
				route: toPath,
				fromRoute: from.fullPath
			}
			window.history.pushState(newState, '', toPath)
		}
		isInitialLoad = false
	})

	// 모달 오픈 시 더미 스택 푸시
	function pushModalHistory() {
		const raw = router.currentRoute.value.fullPath || window.location.pathname || '/'
		const currentPath = raw.startsWith('/') ? raw : `/${raw}`

		const newState = {
			...window.history.state,
			nuxt: true,
			isModal: true,
			currentRoute: currentPath
		}
		window.history.pushState(newState, '', currentPath)
	}

	const observer = new MutationObserver(mutations => {
		for (const m of mutations) {
			for (const node of Array.from(m.addedNodes)) {
				if (node instanceof HTMLElement && node.matches(modalSelector)) {
					const style = window.getComputedStyle(node)
					if (style.display !== 'none' && style.visibility !== 'hidden') {
						pushModalHistory()
						return
					}
				}
			}
		}
	})
	observer.observe(document.body, { childList: true, subtree: true })

	const onPop = async (event: PopStateEvent) => {
		const modals = document.querySelectorAll(modalSelector)

		// 1) 팝업창 뒤로가기 - 가장 위에 있는 모달만 닫기
		if (modals.length > 0) {
			// z-index가 가장 높은 모달 찾기
			let topModal: Element | null = null
			let highestZIndex = -1

			modals.forEach(modal => {
				const computedStyle = window.getComputedStyle(modal as Element)
				const zIndex = parseInt(computedStyle.zIndex) || 0

				if (zIndex > highestZIndex) {
					highestZIndex = zIndex
					topModal = modal
				}
			})

			// 가장 위에 있는 모달만 닫기
			if (topModal) {
				const btn = (topModal as HTMLElement).querySelector(
					'[aria-label="Close"], .v-dialog__close, .btn-close, .popup-close, .btn-back'
				)
				if (btn) {
					(btn as HTMLElement).dispatchEvent(new MouseEvent('click'))
				} else {
					(topModal as HTMLElement).remove()
				}
			}
			return
		}

		// 2) 페이지 간 뒤로가기 처리
		if (event.state && event.state.isModal) {
			// 모달 관련 히스토리인 경우 기본 뒤로가기
			router.back()
		} else if (event.state && event.state.fromRoute &&
			typeof event.state.fromRoute === 'string' &&
			event.state.fromRoute !== 'undefined' &&
			event.state.fromRoute !== '' &&
			event.state.fromRoute !== '/' &&
			event.state.fromRoute.length > 0 &&
			event.state.fromRoute.startsWith('/')) {
			// 이전 페이지로 이동 (유효한 경로인지 확인)
			try {
				router.push(event.state.fromRoute)
			} catch (error) {
				console.warn('Invalid route, preventing infinite loop:', event.state.fromRoute)
				router.back()
			}
		} else if (event.state && event.state.back &&
			typeof event.state.back === 'string' &&
			event.state.back.startsWith('/')) {
			// 브라우저 자체 히스토리 상태인 경우 back 경로로 이동
			try {
				router.push(event.state.back)
			} catch (error) {
				console.warn('Invalid back route:', event.state.back)
				router.back()
			}
		} else {
			// 기본 뒤로가기
			router.back()
		}
	}

	window.addEventListener('popstate', onPop)
})